import type { StudentSkillBelow, StudentSkillRow, StudentSkillsResponse } from "@edu/shared";
import { byTasksText, dayCountText, exerciseCountText } from "../../lib/plurals.ts";

/**
 * Слова панели «Где дыры»: то, что панель считает сама, и то, что просто стоит
 * на экране, без React.
 *
 * ⚠️ **Ни одного импорта значением из `@edu/shared/oge/*`.** Диагноз целиком
 * считает сервер, панель получает готовые слова (1.4 и 7.5
 * docs/student-errors-design.md): словарь навыков, граф рёбер и рецепты
 * мисконцепций в бандл не едут ни одним путём, потому что карточка ученика это
 * НЕ ленивый чанк, и её вес платят все, включая учеников. Здесь допустимы
 * только типы (`import type`) и данные из ответа ручки.
 *
 * Отдельным модулем от компонентов по правилу раздела 9 CLAUDE.md: тестов на
 * компоненты в проекте нет, а формулировки и выбор состояния проверить надо, и
 * проверяются они только тогда, когда живут в чистой функции.
 */

// ---------------------------------------------------------------------------
// Слова панели, которые не считаются, а просто стоят.
//
// ⚠️ Здесь, а не литералами в JSX, с 10.09.2026 (5.2 docs/tour-demo-design.md).
// Демо на странице обзора рисует тот же механизм без React: взять строку из
// компонента оно не может, а переписать копией не должно, копия разошлась бы
// молча первой же правкой. Панель и демо берут слова отсюда, и замок демо
// проверяет, что в `src/tour/` ни одна из них не стоит литералом.
//
// Правило состава: строка, которая собирается из данных (число, список
// названий), живёт функцией ниже, а не константой с подстановкой по месту.
// Чего здесь нет намеренно: «Повторить» у отказа перечитывания. Это общее слово
// всех панелей кабинета, а не слово этой, и демо оно не нужно.
// ---------------------------------------------------------------------------

/** Заголовок панели. Демо им не называется: оно показывает процесс, а не итог. */
export const PANEL_TITLE = "Где дыры";

/** Холодный старт: попыток не было. Это приглашение, а не пустой блок (7.2 проекта). */
export const COLD_TEXT = "Заданий, по которым можно что-то сказать, ещё не было.";
/** Мало данных: от «всё держится» отличается размером выборки, и слово это говорит. */
export const THIN_TEXT = "Заданий пока мало, чтобы что-то утверждать.";
/** Провалов нет. */
export const HELD_TEXT = "Всё, что проверяли, держится.";

/** Заголовок следствий под корнями. */
export const CONSEQUENCES_HEAD = "Что ещё не берётся";
/** Заголовок того, что в порядке: блок нужен, чтобы отсутствие строки не читалось провалом. */
export const HELD_HEAD = "Держится";

/**
 * Заголовок тем под корнем, которых не измеряли. Это и есть оговорка «корень
 * это глубочайшее из ПРОВЕРЕННОГО», сказанная заголовком, а не отдельной строкой.
 */
export const BELOW_HEAD = "Глубже мы не проверяли";
/** Кнопка у темы под корнем, по которой задания у платформы есть. */
export const BELOW_ACTION = "Проверить и это";
/** Подпись у темы под корнем без кнопки: честно, без обещаний и без даты. */
export const BELOW_NO_TASKS = "Заданий по этой теме у платформы пока нет.";

/** Слот класса занят словами и тогда, когда класса нет: пустой слот схлопывается. */
export const GRADE_UNKNOWN = "класс не указан";

/** Кнопка сборки листа у холодного старта, у тонкой выборки и у найденных корней. */
export const SHEET_ACTION = "Собрать диагностический лист";
/** Та же кнопка у «всё держится»: проверять тут нечего, можно только расширить. */
export const NEW_TOPIC_ACTION = "Проверить новую тему";
/** Вопрос над выбором темы: тот, на который у преподавателя ответ есть всегда. */
export const SHEET_QUESTION = "Какую тему собираетесь вести";
export const SHEET_PLACEHOLDER = "выберите тему";
export const SHEET_BUSY = "Собираем…";
/** Что именно соберётся: лист идёт не по самой теме, а по тому, на чём она стоит. */
export const SHEET_NOTE =
  "Лист собирается по темам, на которых стоит выбранная: после него видно, на каком этаже " +
  "ученик буксует.";

/**
 * Граница покрытия, внизу панели и всегда (3.2 проекта, случай В). Без неё
 * отсутствие геометрии читается как «с геометрией всё в порядке».
 */
export const COVERAGE_NOTE =
  "Считается по темам, по которым платформа умеет давать задания: числа и алгебра. Геометрии и " +
  "текстовых задач здесь нет.";

/** Надёжность словом. */
type Confidence = StudentSkillRow["confidence"];

/**
 * ⚠️ Слова про РАЗМЕР ВЫБОРКИ, а не про глубину провала (2.1 проекта). «Неверно
 * 1 из 1» это предположение, а не приговор, и слово рядом с числами обязано
 * читаться именно так. Поэтому здесь «предположение», а не «слабо»: второе
 * читается как оценка ученика, а оценивается тут наша уверенность.
 */
const CONFIDENCE: Record<Confidence, string> = {
  weak: "предположение",
  medium: "похоже",
  strong: "уверенно",
};

export function confidenceWord(confidence: Confidence): string {
  return CONFIDENCE[confidence];
}

/**
 * «неверно 1 из 6».
 *
 * ⚠️ Стоит у КАЖДОЙ строки панели, включая те, что держатся. Вердикт без
 * знаменателя это то же самое, что процент верных ответов, за который мы ругаем
 * чужие продукты: «0 из 0» и «0 из 40» это разные сообщения (2.1 проекта).
 */
export function wrongText(wrong: number, tasks: number): string {
  return `неверно ${wrong} из ${tasks}`;
}

/**
 * Подпись шапки: по чему это посчитано.
 *
 * «По первому ответу» здесь, а не у каждой строки: правило одно на всю панель
 * (1.2 проекта), а повторённое двадцать раз оно перестаёт читаться. Пустое окно
 * названо словами, потому что «посчитано по 0 заданиям» это не подпись, а
 * загадка.
 */
export function countedText(measured: number, days: number): string {
  if (measured === 0) return `заданий за ${dayCountText(days)} не было`;
  return `посчитано ${byTasksText(measured)} за ${dayCountText(days)}, по первому ответу на каждое`;
}

/**
 * Первая строка панели обычными словами: «Не берётся X, потому что не даётся Y».
 *
 * Собирается КЛИЕНТОМ по шаблону предмета, а не сервером (решение 1 раскладки
 * 7.1): у русского шаблон другой, и держать склонения в двух местах хуже, чем
 * один шаблон на предмет. Названия тем берутся в кавычки именно затем, чтобы
 * шаблон не пытался их склонять: «Обыкновенные дроби: действия» в родительном
 * падеже не существует ни в одном словаре, который у нас есть.
 */
export function rootPhrase(rootName: string, fromNames: readonly string[]): string {
  const root = `«${rootName}»`;
  if (fromNames.length === 0) return `Не даётся ${root}.`;
  const from = fromNames.map((n) => `«${n}»`).join(", ");
  const verb = fromNames.length === 1 ? "Не берётся" : "Не берутся";
  return `${verb} ${from}, потому что не даётся ${root}.`;
}

/**
 * Подпись у следствия: откуда оно идёт.
 *
 * Без неё преподаватель прочитает три темы как три задачи, а задача одна
 * (решение 2 раскладки 7.1).
 */
export function originText(rootNames: readonly string[]): string {
  if (rootNames.length === 0) return "";
  return `идёт от ${rootNames.map((n) => `«${n}»`).join(" и ")}`;
}

/** Слово с большой буквы: подпись блока приезжает данными и в нижнем регистре. */
export function capitalized(word: string): string {
  return word.length === 0 ? word : word[0]!.toUpperCase() + word.slice(1);
}

/**
 * «Не проверяли: A · B»: темы, по которым задания есть, а ученику их не давали.
 *
 * Пустой список это пустая строка, а не «Не проверяли:» с ничем: строки без
 * тем на экране нет вовсе.
 */
export function unmeasuredText(names: readonly string[]): string {
  if (names.length === 0) return "";
  return `Не проверяли: ${names.join(" · ")}`;
}

/** «3 задания из 6»: в скольких заданиях окна встретилась названная ошибка. */
export function misconceptionCountText(hits: number, tasks: number): string {
  return `${exerciseCountText(hits)} из ${tasks}`;
}

/** Вид ребра под корнем словами преподавателя: жёсткое и мягкое, как в графе. */
export function prereqKindText(kind: StudentSkillBelow["kind"]): string {
  return kind === "hard" ? "без этого тема не берётся" : "без этого будет тяжело";
}

/**
 * Состояние панели (7.2 проекта).
 *
 * `cold` и `thin` это не пустота, а приглашение, поэтому блок в них есть. Пустой
 * панели, которая есть и ничего не говорит, здесь нет вовсе: пустое место на
 * экране читается как «сюда надо что-то внести».
 */
export type SkillsView = "cold" | "thin" | "held" | "roots";

export function viewOf(data: StudentSkillsResponse): SkillsView {
  if (data.measured === 0 || data.skills.length === 0) return "cold";
  // Провал есть: показываем раскладку 7.1 даже тогда, когда спуск не нашёл
  // корня. Проваленная тема без корня это всё ещё то, ради чего сюда пришли
  if (data.skills.some((s) => s.failed)) return "roots";
  return data.skills.every((s) => s.tasks < 2) ? "thin" : "held";
}

/**
 * Состав диагностического листа в адрес формы выдачи: `слаг:уровень:количество`
 * через запятую (6.3 проекта).
 *
 * ⚠️ Форма этот параметр сегодня не читает: второй вход в неё это следующий шаг
 * и чужая работа. Строку мы всё равно собираем по описанному формату, чтобы
 * панель не пришлось править, когда вход появится.
 */
export function picksParam(
  picks: readonly { skill: string; difficulty: number; count: number }[],
): string {
  return picks.map((p) => `${p.skill}:${p.difficulty}:${p.count}`).join(",");
}
